import React, { useState } from 'react';

import { Button, Flex } from '@mantine/core';
import { IconFolder, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { BatchResourceSelector } from 'components/BatchResourceSelector/BatchResourceSelector';
import { ChipFilters, Filter } from 'components/ChipFilters';
import { DashboardPageWrapper } from 'components/DashboardPageWrapper';
import { ErrorAlert } from 'components/ErrorAlert';
import { ResourceLoadingSkeleton } from 'components/LoadingSkeletons/ResourceLoadingSkeleton';
import { NoDataFound } from 'components/NoDataFound';
import { ResourceTile } from 'components/ResourceTile';
import { SearchBar } from 'components/SearchBar';
import { TeamSelectFilter } from 'components/TeamSelectFilter';
import { VirtualListScrollArea } from 'components/VirtualListScrollArea';
import { useUserContext } from 'contexts/UserContext';
import { GitbookIcon } from 'hugeicons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { NotificationService } from 'services/notification-service';
import { ResourceService } from 'services/resource-service';
import { TeamService } from 'services/team-service';
import { UserService } from 'services/user-service';
import { RESOURCE_BATCH_UPLOAD_NOTIF_ID } from 'shared/constants/notification-ids';
import { RESOURCES_QUERY_KEY } from 'shared/constants/query-keys';
import { MY_RESOURCES_FILTER } from 'shared/constants/resource';
import { Resource, ResourceAiProcessingStatus, ResourceType, UnUploadedResource } from 'shared/interfaces/Resource';
import { openResource } from 'shared/utils/resource';

const LOCAL_STORAGE_FILTER_KEY = 'resource-team-filter';

export function ResourcesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [user] = useUserContext();

  const [fetchPeriodically, setFetchPeriodically] = useState(false);
  const [tagFilters, setTagFilters] = useState(new Set<string>([]));
  const [selectedTagFilters, setSelectedTagFilters] = useState(new Set<string>([]));
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<Filter<ResourceType>[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<Filter<ResourceType>>();

  const [batchResourceSelectorOpened, setBatchResourceSelectorOpened] = useState(false);

  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>(
    localStorage.getItem(LOCAL_STORAGE_FILTER_KEY) ?? MY_RESOURCES_FILTER
  );

  // Mutations
  const batchCreateResourcesMutation = useMutation({
    mutationFn: ResourceService.batchUploadResource,
    onMutate: () => {
      setSelectedTeamFilter(MY_RESOURCES_FILTER);
      setFetchPeriodically(true);
      NotificationService.startAction(RESOURCE_BATCH_UPLOAD_NOTIF_ID, 'FEED_BACK.RESOURCES.LOADING.BATCH_UPLOAD');
    },
    onSuccess: (newResources: Resource[]) => {
      addResources(newResources);
      // Keep the notification for 5 second
      setTimeout(() => {
        NotificationService.hideNotification(RESOURCE_BATCH_UPLOAD_NOTIF_ID);
      }, 5000);
    },
    onError: (error: AxiosError) => {
      NotificationService.endAction(
        undefined,
        RESOURCE_BATCH_UPLOAD_NOTIF_ID,
        'FEED_BACK.RESOURCES.FAILURE.BATCH_UPLOAD.GENERIC',
        error
      );
    }
  });

  // Queries
  const filteredResourcesQuery = useQuery({
    queryKey: [RESOURCES_QUERY_KEY, selectedTeamFilter],
    refetchInterval: () => (fetchPeriodically ? 5000 : 0), // 5 seconds
    queryFn: async () => {
      if (selectedTeamFilter === MY_RESOURCES_FILTER) {
        return ResourceService.getAllResourcesCreatedByMe();
      } else if (UserService.isCustomOrganisationMember()) {
        const teams = await TeamService.getMemberTeams();
        const team = teams.find((team) => team.name === selectedTeamFilter);
        if (team) {
          return ResourceService.getAllResourcesByTeam(team._id);
        } else {
          setSelectedTeamFilterAndUpdateLocalStorage(MY_RESOURCES_FILTER);
        }
      }

      return [];
    },
    onSuccess: (resources: Resource[]) => {
      // If all resources are processed and nothing is loading, stop fetching periodically.
      if (batchCreateResourcesMutation.isLoading) return;
      const tags = new Set<string>();
      let shouldFetchPeriodically = false;
      const types = new Set<ResourceType>();
      for (const resource of resources) {
        if (resource.aiProcessingStatus === ResourceAiProcessingStatus.IN_PROGRESS) {
          shouldFetchPeriodically = true;
        }

        resource.tags.forEach((tag) => tags.add(tag));
        types.add(resource.type);
      }
      setFetchPeriodically(shouldFetchPeriodically);
      setTagFilters(tags);
      setFilters(
        Array.from(types)
          .sort((a, b) => a.localeCompare(b))
          .map((type) => ({ label: t(`RESOURCE_TYPES.${type.toUpperCase()}`), value: type }))
      );
    }
  });

  const onBatchUpload = (resources: UnUploadedResource[]) =>
    batchCreateResourcesMutation.mutate({
      resources,
      addResources,
      notificationId: RESOURCE_BATCH_UPLOAD_NOTIF_ID,
      notificationTitleKey: 'FEED_BACK.RESOURCES.LOADING.BATCH_UPLOAD'
    });

  const addResources = (resources: Resource[], override = true) => {
    queryClient.setQueryData([RESOURCES_QUERY_KEY, selectedTeamFilter], (existingResources: Resource[] = []) => {
      const allRessources = new Map<string, Resource>();
      for (const resource of existingResources) {
        allRessources.set(resource._id, resource);
      }
      for (const resource of resources) {
        const existingResource = allRessources.get(resource._id);
        if (existingResource && existingResource.aiProcessingStatus === ResourceAiProcessingStatus.DONE) {
          continue;
        }
        if (!override && existingResource) continue;
        allRessources.set(resource._id, resource);
      }
      return Array.from(allRessources.values()).sort(
        (a, b) => (b.updatedAt ?? b.createdAt) - (a.updatedAt ?? a.createdAt)
      );
    });
  };

  const setSelectedTeamFilterAndUpdateLocalStorage = (teamFilter: string) => {
    // Only save the filter if it's not the default one that is localized
    if (teamFilter !== MY_RESOURCES_FILTER) {
      localStorage.setItem(LOCAL_STORAGE_FILTER_KEY, teamFilter);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_FILTER_KEY);
    }
    setSelectedTeamFilter(teamFilter);
  };

  const CreateButton = () => (
    <Button leftIcon={<IconPlus />} onClick={() => setBatchResourceSelectorOpened(true)}>
      {t('APP.ACTIONS.ADD')}
    </Button>
  );

  if (filteredResourcesQuery.isError) return <ErrorAlert />;

  const resourcesLoading = filteredResourcesQuery.isLoading;
  const noResources = !resourcesLoading && filteredResourcesQuery.data.length === 0;

  const filteredResources = (filteredResourcesQuery.data ?? [])
    .filter((resource) =>
      selectedTagFilters.size > 0 ? resource.tags.some((tag) => selectedTagFilters.has(tag)) : true
    )
    .filter((resource) => (searchValue ? resource.fileName.toLowerCase().includes(searchValue.toLowerCase()) : true))
    .filter((resource) => (selectedFilter?.value ? resource.type === selectedFilter.value : true));

  return (
    <>
      <DashboardPageWrapper
        icon={<GitbookIcon size={25} />}
        title={t('APP.DASHBOARD_SECTIONS.RESOURCES')}
        rightComponent={
          <Flex direction="row" align="center" gap="md">
            {UserService.isCustomOrganisationMember() && (
              <TeamSelectFilter
                teamFilter={selectedTeamFilter}
                setTeamFilter={setSelectedTeamFilterAndUpdateLocalStorage}
                myFilter={MY_RESOURCES_FILTER}
              />
            )}
            {!noResources && <CreateButton />}
          </Flex>
        }>
        <Flex h="100%" direction="column">
          {(resourcesLoading || filteredResourcesQuery.data.length > 0) && (
            <>
              <ChipFilters
                py={10}
                filters={filters}
                selectedFilter={selectedFilter}
                setSelectedFilter={(filter) => setSelectedFilter(filter)}
              />
              <SearchBar
                mb={10}
                filters={tagFilters}
                selectedFilters={selectedTagFilters}
                setSelectedFilters={setSelectedTagFilters}
                onFilterCreate={() => filteredResourcesQuery.refetch()}
                onSearchValueChange={setSearchValue}
                placeholder={t('SEARCH_BAR.RESOURCE_PLACEHOLDER')}
                resources={filteredResourcesQuery.data}
              />
            </>
          )}

          {resourcesLoading ? (
            <ResourceLoadingSkeleton />
          ) : (
            <VirtualListScrollArea
              scrollbarSize={0}
              h={noResources || filteredResources.length === 0 ? 0 : '100%'}
              items={filteredResources.map((resource, index) => (
                <ResourceTile
                  key={resource._id}
                  mt={index === 0 ? 0 : 5}
                  mb={5}
                  resource={resource}
                  onResourceClick={() => openResource(resource, navigate)}
                  optionsEnabled={resource.creator === user._id}
                  allTags={Array.from(tagFilters)}
                />
              ))}
            />
          )}

          {noResources && (
            <NoDataFound
              title={t('NO_DATA_FOUND.RESOURCES.TITLE')}
              subTitle={t('NO_DATA_FOUND.RESOURCES.SUB_TITLE')}
              icon={<IconFolder />}
              button={<CreateButton />}
              h="100%"
            />
          )}

          {!noResources && filteredResources.length == 0 && (selectedTagFilters.size > 0 || searchValue) && (
            <NoDataFound h="100%" />
          )}
        </Flex>
      </DashboardPageWrapper>

      <BatchResourceSelector
        opened={batchResourceSelectorOpened}
        onBatchUpload={onBatchUpload}
        onClose={() => setBatchResourceSelectorOpened(false)}
      />
    </>
  );
}
